import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useRoleApi } from '../../services/api/useRole.api.ts';
import { useUserApi } from '../../services/api/useUser.api.ts';
import { Role } from '../../types/Role/Role.type.ts';
import { User } from '../../types/User/User.type.ts';

function TeamMemberTable({
  activeMember,
  handleDeactivateUser,
}: {
  activeMember: User;
  handleDeactivateUser: (email: string) => void;
}) {
  const { getRoles } = useRoleApi();
  const { getUserInfo } = useUserApi();

  const { data: userInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });
  const user_id = userInfo?.id;

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteConfirm = () => {
    handleDeactivateUser(activeMember.email);
    setOpen(false);
  };

  return (
    <TableRow key={activeMember.id} hover>
      <TableCell>
        <Stack
          direction='row'
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Avatar src={activeMember.profile_picture_url || ''}>
            {activeMember.first_name?.charAt(0) || activeMember.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Stack>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 'medium',
              }}
            >
              {activeMember.first_name} {activeMember.last_name}
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
              }}
            >
              {activeMember.email}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell width={140}>
        <Select
          sx={{
            minWidth: '180px',
            borderRadius: '8px',
            border: '1px solid #cbd5e0',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
          input={<InputBase />}
          value={activeMember.role.id}
          disabled={activeMember.id === user_id}
        >
          {roles?.map((role: Role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <IconButton onClick={handleClickOpen} disabled={activeMember.id === user_id}>
          <Trash2Icon size={'20px'} />
        </IconButton>
      </TableCell>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle sx={{ fontSize: '16px' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            Are you sure you want to delete user {activeMember.first_name} {activeMember.last_name}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
}

export default TeamMemberTable;
